import {
  ArrowLeftIcon,
  MicrophoneIcon,
  SpeakerWaveIcon,
  StarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Button, Typography } from '@material-tailwind/react';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../../store/hook';
import { reportFreeTrialStepProgress } from '../../api/utils/free-trial/api';
import { openAlert } from '../../store/features/modalSlice';
import { getErrorMessages } from '../../api/utils/util-func';
import FreeTrialAttentionEffect from './FreeTrialAttentionEffect';

interface FreeTrialTutorialViewProps {
  onShowLesson: () => void;
}

const TOTAL_TUTORIAL_STEPS = 5;

/* 시작 전 안내 */
const FreeTrialTutorialView = ({
  onShowLesson,
}: FreeTrialTutorialViewProps) => {
  const dispatch = useAppDispatch();

  const [tutorialStep, setTutorialStep] = useState(1);

  const linkToken = sessionStorage.getItem('freeTrialLinkToken');

  useEffect(() => {
    if (!linkToken) return;

    const reportTutorialProgress = async () => {
      try {
        await reportFreeTrialStepProgress({
          linkToken,
          step: 'tutorial',
        });
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
        console.error('튜토리얼 진행 상태 저장 실패', error);
      }
    };

    void reportTutorialProgress();
  }, [linkToken]);

  const handleStartLesson = () => {
    onShowLesson();
  };

  const handlePrev = () => {
    setTutorialStep((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    if (tutorialStep < TOTAL_TUTORIAL_STEPS) {
      setTutorialStep((prev) => prev + 1);
      return;
    }

    void handleStartLesson();
  };

  const handleSkip = () => {
    void handleStartLesson();
  };

  return (
    <section className="mt-5 overflow-hidden rounded-2xl bg-white shadow-[0_6px_20px_rgba(40,25,100,0.05)]">
      <TutorialProgress
        currentStep={tutorialStep}
        totalStep={TOTAL_TUTORIAL_STEPS}
        onSkip={handleSkip}
      />

      <div className="grid min-h-[520px] gap-8 px-5 py-10 md:grid-cols-2 md:items-center md:px-10 md:py-14">
        <TutorialVisual step={tutorialStep} />

        <TutorialDescription
          step={tutorialStep}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      </div>
    </section>
  );
};

interface TutorialProgressProps {
  currentStep: number;
  totalStep: number;
  onSkip: () => void;
}

const TutorialProgress = ({
  currentStep,
  totalStep,
  onSkip,
}: TutorialProgressProps) => {
  return (
    <div className="border-b border-gray-100 px-5 py-4">
      {/* 모바일 / 태블릿 */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between gap-3">
          <div className="whitespace-nowrap rounded-full bg-[#6737F4] px-3 py-1.5 text-[11px] font-bold text-white">
            시작 전 1분 안내
          </div>

          <button
            type="button"
            onClick={onSkip}
            className="shrink-0 whitespace-nowrap text-[11px] font-bold text-[#6737F4] underline underline-offset-2"
          >
            건너뛰고 수업하기
          </button>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {Array.from({ length: totalStep }, (_, index) => (
              <div
                key={index}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  index < currentStep ? 'bg-[#6737F4]' : 'bg-[#E5E1F2]'
                }`}
              />
            ))}
          </div>

          <Typography className="shrink-0 whitespace-nowrap text-[11px] font-bold text-gray-700">
            {currentStep} / {totalStep}
          </Typography>
        </div>
      </div>

      {/* PC */}
      <div className="hidden grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-4 lg:grid">
        <div className="whitespace-nowrap rounded-full bg-[#6737F4] px-3 py-1.5 text-[12px] font-bold text-white">
          시작 전 1분 안내
        </div>

        <div className="flex min-w-0 items-center gap-1.5">
          {Array.from({ length: totalStep }, (_, index) => (
            <div
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors ${
                index < currentStep ? 'bg-[#6737F4]' : 'bg-[#E5E1F2]'
              }`}
            />
          ))}
        </div>

        <Typography className="whitespace-nowrap text-[12px] font-bold text-gray-700">
          {currentStep} / {totalStep}
        </Typography>

        <button
          type="button"
          onClick={onSkip}
          className="whitespace-nowrap text-[12px] font-bold text-[#6737F4] underline underline-offset-2"
        >
          건너뛰고 수업하기
        </button>
      </div>
    </div>
  );
};

export default FreeTrialTutorialView;

const TutorialVisual = ({ step }: { step: number }) => {
  switch (step) {
    case 1:
      return <CourseIntroVisual />;

    case 2:
      return <PictureMemoryVisual />;

    case 3:
      return <CurriculumVisual />;

    case 4:
      return <LearningFlowVisual />;

    case 5:
      return <ReadyVisual />;

    default:
      return null;
  }
};

const CourseIntroVisual = () => {
  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col items-center justify-center rounded-3xl bg-[#F1EDFF] px-6 py-12">
      <Typography className="text-[12px] font-bold text-[#7F66D7]">
        BASIC COURSE
      </Typography>

      <Typography className="mt-1 text-[34px] font-black text-[#4622C7] md:text-[36px]">
        잉스바
      </Typography>

      <Typography className="mt-1 text-[14px] font-semibold text-gray-800">
        English Story Bible
      </Typography>

      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {['바이블 스토리', '영어 암송', '마음 판에 새기기'].map((item) => (
          <span
            key={item}
            className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-700 md:text-[12px]"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

const PictureMemoryVisual = () => {
  return (
    <div className="mx-auto w-full max-w-[430px]">
      <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-[#E5DFFF] bg-white">
        <img
          src={
            process.env.PUBLIC_URL +
            '/images/free-trial/story_main_for_tutorial.jpeg'
          }
          alt="삽화기억술 예시"
          className="h-full w-full object-contain p-1"
        />
      </div>
    </div>
  );
};

const CurriculumVisual = () => {
  return (
    <div className="mx-auto w-full max-w-[430px]">
      <div className="space-y-3">
        <CurriculumRow label="Book 1">
          <div className="flex w-fit min-w-0 items-center gap-2 md:w-[52%]">
            <div className="whitespace-nowrap rounded-lg bg-[#6737F4] px-3 py-2 text-[10px] font-bold text-white">
              On the seventh day,
            </div>

            <span className="shrink-0 whitespace-nowrap rounded-full bg-[#FFD928] px-2.5 py-1 text-[10px] font-extrabold text-[#5F4300]">
              오늘!
            </span>
          </div>
        </CurriculumRow>

        <CurriculumRow label="Book 2">
          <div className="flex w-[78%] min-w-0 gap-1 md:w-[68%]">
            <div className="h-7 basis-[58%] rounded-lg bg-[#B19AFF]" />
            <div className="h-7 flex-1 rounded-lg bg-[#C5B3FF]" />
          </div>
        </CurriculumRow>

        <CurriculumRow label="Book 3">
          <div className="flex w-[90%] min-w-0 gap-1 md:w-[84%]">
            <div className="h-7 basis-[55%] rounded-lg bg-[#C6B5FF]" />
            <div className="h-7 flex-1 rounded-lg bg-[#D6C9FF]" />
          </div>
        </CurriculumRow>

        <div className="grid grid-cols-[58px_minmax(0,1fr)] items-center gap-3 md:gap-4">
          <div className="text-[10px] font-bold leading-4 text-gray-700">
            구약 24권
            <br />
            신약 24권
          </div>

          <div className="flex min-w-0 gap-1">
            {Array.from({ length: 10 }, (_, index) => (
              <div
                key={index}
                className={`h-7 min-w-0 flex-1 bg-[#E8E2FF] ${
                  index === 0
                    ? 'rounded-l-lg'
                    : index === 9
                      ? 'rounded-r-lg'
                      : ''
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-[#F5F1FF] px-4 py-3">
        <Typography className="text-[11px] font-extrabold text-[#5941B9]">
          갈수록 다양해지는 문장 구조
        </Typography>

        <div className="mt-2 flex flex-wrap gap-2">
          {['관계대명사', '현재분사', '가정법', '과거완료'].map((item) => (
            <span
              key={item}
              className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-gray-700"
            >
              {item}
            </span>
          ))}

          <span className="text-[12px] font-bold text-gray-700">...</span>
        </div>
      </div>
    </div>
  );
};

const CurriculumRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="grid grid-cols-[58px_minmax(0,1fr)] items-center gap-3 md:gap-4">
      <Typography className="text-[14px] font-black text-[#5536D6]">
        {label}
      </Typography>

      <div className="min-w-0">{children}</div>
    </div>
  );
};

const LearningFlowVisual = () => {
  const items = [
    {
      step: '1',
      title: '수업하기',
      description: '엄마와 함께 약 15분',
    },
    {
      step: '2',
      title: '온라인 복습',
      description: '젭스온에서 아이 혼자',
    },
    {
      step: '3',
      title: 'AI 인증제',
      description: '말로 암송하고 확인받기',
    },
  ];

  return (
    <div className="mx-auto w-full max-w-[430px] space-y-2">
      {items.map((item) => (
        <div
          key={item.step}
          className="flex items-center gap-3 rounded-xl bg-[#FFF4CF] px-3 py-2.5"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#6737F4] text-[13px] font-black text-white">
            {item.step}
          </div>

          <Typography className="text-[12px] text-gray-800">
            <span className="font-extrabold">{item.title}</span>
            <span className="mx-1.5">·</span>
            <span>{item.description}</span>
          </Typography>
        </div>
      ))}

      <div className="flex items-center gap-3 rounded-xl bg-[#211B2D] px-3 py-2.5 text-white">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFD928] text-[#493600]">
          <StarIcon className="h-4 w-4 fill-current" />
        </div>

        <Typography className="text-[12px] font-extrabold text-white">
          결과 리포트 + 우리 아이 암송 유형
        </Typography>
      </div>
    </div>
  );
};

const ReadyVisual = () => {
  const items = [
    {
      icon: <SpeakerWaveIcon className="h-5 w-5" />,
      title: '소리를 켜 주세요',
      description: '음원이 자동으로 나와요',
    },
    {
      icon: <UserGroupIcon className="h-5 w-5" />,
      title: '아이와 나란히 앉아요',
      description: '엄마가 화면을 넘겨요',
    },
    {
      icon: <MicrophoneIcon className="h-5 w-5" />,
      title: 'AI 인증 땐 마이크',
      description: '조용한 곳에서 응시해요',
    },
  ];

  return (
    <div className="mx-auto w-full max-w-[430px] space-y-2">
      {items.map((item) => (
        <div
          key={item.title}
          className="flex items-center gap-3 rounded-xl border border-[#DCD3FF] bg-white px-4 py-3"
        >
          <div className="shrink-0 text-[#6737F4]">{item.icon}</div>

          <Typography className="text-[12px] text-gray-800">
            <span className="font-extrabold">{item.title}</span>
            <span className="mx-1.5">·</span>
            <span>{item.description}</span>
          </Typography>
        </div>
      ))}
    </div>
  );
};

interface TutorialDescriptionProps {
  step: number;
  onPrev: () => void;
  onNext: () => void;
}

const TutorialDescription = ({
  step,
  onPrev,
  onNext,
}: TutorialDescriptionProps) => {
  const content = getTutorialDescription(step);

  return (
    <div>
      <div className="flex items-center gap-2">
        <img
          src="/images/free-trial/bready.png"
          alt=""
          className="h-7 w-7 object-contain"
        />

        <div className="inline-flex items-center rounded-full rounded-bl-none bg-[#F4F0FF] px-3 py-1">
          <Typography className="text-[11px] font-bold text-[#6737F4] md:text-[12px]">
            {content.tip}
          </Typography>
        </div>
      </div>

      <Typography className="mt-2 text-[22px] font-black leading-[1.35] text-gray-900 md:text-[26px]">
        {content.title}
      </Typography>

      <Typography className="mt-4 text-[13px] leading-6 text-gray-700 md:text-[14px] md:leading-7">
        {content.description}
      </Typography>

      {content.highlight && (
        <div className="mt-4 rounded-xl bg-[#FFF4CF] px-3 py-2.5">
          <Typography className="text-[11px] font-extrabold text-[#4E38C4] md:text-[12px]">
            {content.highlight}
          </Typography>
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          onClick={onPrev}
          disabled={step === 1}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition ${
            step === 1
              ? 'cursor-default border-gray-200 bg-white text-gray-400'
              : 'border-[#D6CBFF] bg-white text-[#6737F4] hover:bg-[#F4F0FF]'
          }`}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </button>

        <Button
          ripple={false}
          onClick={onNext}
          className={`group relative h-11 flex-1 overflow-visible rounded-xl bg-[#6737F4] py-0 text-sm font-bold normal-case text-white shadow-none transition-all hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none ${
            step === TOTAL_TUTORIAL_STEPS ? '' : ''
          }`}
        >
          <FreeTrialAttentionEffect />

          <span className="relative z-10">
            {step === TOTAL_TUTORIAL_STEPS ? '수업 시작하기' : '다음'}
          </span>
        </Button>
      </div>
    </div>
  );
};

const getTutorialDescription = (step: number) => {
  switch (step) {
    case 1:
      return {
        tip: 'TIP 1 · 오늘의 과목',
        title: '오늘 체험할 과목은 ‘잉스바’예요',
        description:
          '베이직 코스의 잉스바는 바이블 스토리를 영어로 암송하며 말씀을 마음 판에 새기고, 좋은 영어 문장을 심는 과목이에요.',
      };

    case 2:
      return {
        tip: 'TIP 2 · 젭스만의 방법',
        title: '그림이 문장을 기억해요',
        description:
          '정철 선생님의 발명특허 ‘삽화기억술’은 그림과 짝지어진 청크의 소리와 뜻을 그림으로 기억하는 방법이에요.',
        highlight: '그래서 아직 문장을 만들지 못하는 친구도 암송할 수 있어요!',
      };

    case 3:
      return {
        tip: 'TIP 3 · 오늘 배울 범위',
        title: '오늘은 Book 1 · 천지창조',
        description:
          'Book 1은 쉬운 문장으로 시작해요. 구약 24권, 신약 24권으로 이어지면서 관계대명사, 현재분사처럼 더 다양한 구조의 문장을 만나고, 청크의 길이도 점점 길어져요.',
        highlight: '짧은 청크에서 긴 문장까지, 말씀을 따라 영어가 자라요.',
      };

    case 4:
      return {
        tip: 'TIP 4 · 체험 순서',
        title: '학습 → 복습 → 인증, 3단계를 맛보세요',
        description:
          '잉스바를 배우고, 복습하고, 인증까지 받는 실제 학습 순서 그대로예요. 끝까지 하면 결과 리포트와 우리 아이 암송 유형이 나와요.',
      };

    case 5:
      return {
        tip: 'TIP 5 · 준비',
        title: '준비됐나요?',
        description:
          '수업하기는 엄마가 화면을 넘기며 진행해요. 약 15분이면 충분해요.',
      };

    default:
      return {
        tip: '',
        title: '',
        description: '',
      };
  }
};
